/**
 * Product Card Interactions & Variant Management (product-card.js)
 * Handles card preview (desktop inline & mobile drawer), variant size selection,
 * accessibility focus trapping, keyboard navigation, and Add to Cart across all pages.
 */

// 1. Define <custom-variant-radios> Custom Element if not already defined
if (!customElements.get('custom-variant-radios')) {
  class CustomVariantRadios extends HTMLElement {
    constructor() {
      super();

      this.view = this.getAttribute('data-view');
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if ((this.view === 'mobile' && !isMobile) || (this.view === 'desktop' && isMobile)) {
        return;
      }

      this.form = this.closest('form');
      this.gridInner = this.closest('[data-grid-inner]');
      if (!this.form || !this.gridInner) return;

      this.onVariantChange('load');
      this.addEventListener('change', this.onVariantChange.bind(this));
    }

    getVariantsData() {
      if (!this.gridInner) return null;
      const variantJSON = this.gridInner.querySelector('[data-variantJSON]');
      if (!variantJSON) return null;

      try {
        this.variantData = this.variantData || JSON.parse(variantJSON.textContent);
        return this.variantData;
      } catch (error) {
        console.error('CustomVariantRadios Error:', error);
        return null;
      }
    }

    getCurrentVariant() {
      const variantData = this.getVariantsData();
      if (!variantData || !Array.isArray(variantData)) return;

      this.currentVariant = variantData.find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    getOptionsValue() {
      try {
        const fieldsets = Array.from(this.querySelectorAll('fieldset'));
        if (!fieldsets.length) return;

        this.options = fieldsets.map((fieldset) => {
          const checkedRadio = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
          if (!checkedRadio) {
            const firstRadio = fieldset.querySelector('input:not([disabled])');
            if (firstRadio) {
              firstRadio.checked = true;
              return firstRadio.value;
            }
            return null;
          }
          return checkedRadio.value;
        });

        this.options = this.options.filter(option => option !== null);
      } catch (error) {
        console.error('CustomVariantRadios Error:', error);
        this.options = [];
      }
    }

    onVariantChange() {
      if (!this.form || !this.gridInner) return;

      this.getOptionsValue();
      if (!this.options || !this.options.length) return;

      this.getCurrentVariant();

      if (this.currentVariant) {
        this.updateVariantID(this.currentVariant);
        this.renderProductInfo(this.currentVariant, this.form);
      }

      const radios = this.querySelectorAll('input[type="radio"]');
      radios.forEach(radio => {
        radio.setAttribute('tabindex', radio.checked ? '0' : '-1');
      });
    }

    updateVariantID(currentVariant) {
      if (!currentVariant) return;
      this.gridInner.querySelectorAll('[data-preview-var-id]').forEach((element) => {
        if (element) element.setAttribute('data-preview-var-id', currentVariant.id);
      });
      const idInput = this.gridInner.querySelector('input[name="id"]');
      if (idInput) idInput.value = currentVariant.id;
    }

    renderProductInfo(currentVariant, container) {
      if (!currentVariant || !container) return;

      const formatPrice = (cents) => {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(cents, window.money_format);
        }
        if (window._theme?.Helpers?.formatMoney) {
          return _theme.Helpers.formatMoney(cents, window.money_format);
        }
        return '$' + (cents / 100).toFixed(2);
      };

      const price = formatPrice(currentVariant.price);
      const cprice = formatPrice(currentVariant.compare_at_price);

      const salePriceNew = container.querySelector('[data-salePriceNew]');
      const salePriceOld = container.querySelector('[data-salePriceOld]');
      const regularPrice = container.querySelector('[data-regularPrice]');
      const perDiscount = container.querySelector('[data-perDiscount]');
      let discount = currentVariant.compare_at_price - currentVariant.price;
      discount = Math.round(Math.floor(discount) / 100) * 100;

      if (regularPrice) regularPrice.innerHTML = price;
      if (salePriceNew) salePriceNew.innerHTML = price;
      if (salePriceOld) salePriceOld.innerHTML = cprice;

      if (currentVariant.compare_at_price > currentVariant.price && discount > 0) {
        if (perDiscount) perDiscount.innerHTML = `${formatPrice(discount)} OFF`;
      }

      if (currentVariant.featured_image && currentVariant.featured_image != null) {
        const featuredImage = container.querySelector('[data-feauredImage]');
        const updatedSrc = currentVariant.featured_image.src.replace('.png', '_115x.png');
        if (updatedSrc && featuredImage) {
          featuredImage.src = updatedSrc;
        }
      }

      const isOnSale = currentVariant.compare_at_price > currentVariant.price;
      let accessibleText = `Add To Cart ${price}`;
      if (isOnSale) {
        accessibleText = `Add To Cart Sale price ${price} Regular price ${cprice}`;
      }

      const previewAtcBtnDesHtml = `<span aria-hidden="true">Add To Cart</span>
                                    <span aria-hidden="true">${price}</span>
                                    <span aria-hidden="true">${isOnSale ? cprice : ''}</span>
                                    <span class="visually-hidden">${accessibleText}</span>`;

      const previewAtcBtnDes = container.querySelector('.grid-footer .preview-atc-btn') ||
        (this.gridInner ? this.gridInner.querySelector('.grid-footer .preview-atc-btn') : null);
      if (previewAtcBtnDes) {
        if (!currentVariant.available) {
          previewAtcBtnDes.textContent = "Out of Stock - Notify Me";
          previewAtcBtnDes.removeAttribute("aria-label");
          previewAtcBtnDes.removeAttribute("disabled");
          previewAtcBtnDes.onclick = (e) => {
            e.preventDefault();
            (container.querySelector('[data-notify-modal-open]') || (this.gridInner ? this.gridInner.querySelector('[data-notify-modal-open]') : null))?.click();
          };
        } else {
          previewAtcBtnDes.onclick = null;
          previewAtcBtnDes.textContent = '';
          previewAtcBtnDes.innerHTML = previewAtcBtnDesHtml;
          previewAtcBtnDes.removeAttribute("aria-label");
          previewAtcBtnDes.removeAttribute("disabled");
        }
      }

      const previewAtcBtnMob = container.querySelector('.qv-footer .preview-atc-btn');
      if (previewAtcBtnMob) {
        if (!currentVariant.available) {
          previewAtcBtnMob.textContent = "Out of Stock - Notify Me";
          previewAtcBtnMob.removeAttribute("disabled");
          previewAtcBtnMob.onclick = (e) => {
            e.preventDefault();
            (container.querySelector('[data-notify-modal-open]') || (this.gridInner ? this.gridInner.querySelector('[data-notify-modal-open]') : null))?.click();
          };
        } else {
          previewAtcBtnMob.onclick = null;
          previewAtcBtnMob.textContent = "Add to Cart";
          previewAtcBtnMob.removeAttribute("disabled");
        }
      }

      const ozValue = 29.5735;
      const unitConvertArr = (currentVariant.option1 || '').split('oz');
      const selectedVal = currentVariant.option1 || '';
      let selectedValSliced = selectedVal;
      if (selectedVal.includes('oz')) {
        selectedValSliced = selectedVal.slice(0, selectedVal.length - 1);
      }
      if (selectedVal.includes('Tester')) {
        const selectedValArr = selectedVal.split("Tester");
        selectedValSliced = selectedValArr[0].slice(0, selectedValArr[0].length - 2) + " Tester";
      }
      const unitConvertArrValue = Number(unitConvertArr[0]);
      const conversions = { 0.05: 1.5, 0.17: 5, 0.5: 15, 0.8: 25, 1.0: 30, 1.4: 40, 1.7: 50, 2.0: 60, 2.5: 75, 2.7: 80, 3.0: 90, 3.3: 100, 3.4: 100, 4.2: 125, 5.0: 150, 6.7: 200 };
      const unitValue = conversions[unitConvertArrValue] || Math.floor(ozValue * unitConvertArrValue);

      const varTitle = container.querySelector('[data-varTitle]');
      if (varTitle) {
        if (selectedVal.includes('oz')) {
          varTitle.innerHTML = `${selectedValSliced} / ${unitValue}ml`;
        } else {
          varTitle.innerHTML = `${selectedValSliced}`;
        }
      }

      const availableVarQty = currentVariant.inventory_quantity;
      const selectElement = this.form.querySelector('.quantity__select [data-quantity-select]');
      if (selectElement) {
        selectElement.innerHTML = '';
        for (let i = 1; i <= 10; i++) {
          const option = document.createElement('option');
          option.value = i;
          option.textContent = i;
          if (typeof availableVarQty === 'number' && i > availableVarQty) {
            option.disabled = true;
          }
          selectElement.appendChild(option);
        }
      }
    }
  }

  customElements.define('custom-variant-radios', CustomVariantRadios);
}

// 2. Main Product Preview Sizes Manager
window.initProductPreviewSizes = function() {
  if (window.__productPreviewSizesUnifiedInit) return;
  window.__productPreviewSizesUnifiedInit = true;

  // Active modal container getter (desktop or mobile)
  const getActiveContainer = () => {
    const desktopOpen = document.querySelector('[data-preview-size-grid]:not(.hidden)');
    if (desktopOpen) {
      return desktopOpen.querySelector('[role="dialog"]') || desktopOpen;
    }
    const mobileOpen = document.querySelector('.preview-size-qv.active');
    if (mobileOpen) {
      return mobileOpen;
    }
    return null;
  };

  // Tabbables helper for accessibility focus trapping
  const getTabbables = (container) => {
    if (!container) return [];
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"], [tabindex]:not([tabindex="-1"])';
    const elems = container.querySelectorAll(selector);
    return Array.from(elems).filter(el => el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0);
  };

  // Keyboard navigation & focus trap (Tab, Shift+Tab, Arrow keys)
  document.addEventListener('keydown', (e) => {
    // Escape key closing
    if (e.key === 'Escape') {
      const activeDesktop = document.querySelector('[data-preview-size-grid]:not(.hidden)');
      if (activeDesktop) {
        const closeBtn = activeDesktop.querySelector('[data-preview-close-btn]') || activeDesktop.closest('[data-grid-inner]')?.querySelector('[data-preview-close-btn]');
        if (closeBtn) {
          closeBtn.click();
          return;
        }
      }
      const activeMob = document.querySelector('.preview-size-qv.active');
      if (activeMob) {
        const closeMobBtn = activeMob.querySelector('[data-preview-close-mob-btn], .qv-close');
        if (closeMobBtn) {
          closeMobBtn.click();
          return;
        }
      }
    }

    const container = getActiveContainer();
    if (!container) return;

    const tabbables = getTabbables(container);
    if (!tabbables.length) return;

    const first = tabbables[0];
    const last = tabbables[tabbables.length - 1];
    const active = document.activeElement;

    const isLast = active === last || (last && last.contains(active));
    const isFirst = active === first || (first && first.contains(active));

    if (e.key === 'Tab' || e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      if (!container.contains(active) || active === container) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus({ preventScroll: true });
      } else if ((e.shiftKey && isFirst) || (e.key === 'ArrowLeft' && isFirst) || (e.key === 'ArrowUp' && isFirst)) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if ((!e.shiftKey && isLast) || (e.key === 'ArrowRight' && isLast) || (e.key === 'ArrowDown' && isLast)) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    }
  }, true);

  // Focus preservation inside active dialog (focusin)
  document.addEventListener('focusin', (e) => {
    const container = getActiveContainer();
    if (!container) return;
    if (container.contains(e.target) && e.target !== container) return;

    const tabbables = getTabbables(container);
    if (tabbables.length) {
      tabbables[0].focus({ preventScroll: true });
      setTimeout(() => {
        const currentActive = document.activeElement;
        if (!container.contains(currentActive) || currentActive === container) {
          tabbables[0].focus({ preventScroll: true });
        }
      }, 0);
    }
  }, true);

  // Focus preservation inside active dialog (focusout)
  document.addEventListener('focusout', () => {
    const container = getActiveContainer();
    if (!container) return;
    setTimeout(() => {
      const currentActive = document.activeElement;
      if (!container.contains(currentActive) || currentActive === container) {
        const tabbables = getTabbables(container);
        if (tabbables.length) {
          tabbables[0].focus({ preventScroll: true });
        }
      }
    }, 10);
  }, true);

  // Keyboard Space / Enter support on close buttons for ADA compliance
  document.addEventListener('keydown', (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      const closeBtn = e.target.closest('[data-preview-close-btn], [data-preview-close-mob-btn], .preview-size-qv .qv-close');
      if (closeBtn) {
        e.preventDefault();
        closeBtn.click();
      }
    }
  });

  // Sidecart focus management on ATC
  let atcTrigger = null, atcTimer = null;
  const restoreAtcFocus = () => {
    clearInterval(atcTimer);
    if (!atcTrigger) return;
    const t = atcTrigger;
    atcTrigger = null;
    setTimeout(() => { if (document.body.contains(t)) t.focus(); }, 100);
  };

  document.addEventListener('click', (e) => {
    if (atcTrigger && e.target.closest('#dropdown-cart .close-cart, .wrapper-overlay')) {
      restoreAtcFocus();
    }
  }, true);

  document.addEventListener('keydown', (e) => {
    if (atcTrigger && e.key === 'Escape' && document.documentElement.classList.contains('cart-show')) {
      restoreAtcFocus();
    }
  }, true);

  // Global Delegated Click Handler for all Preview Size interactions
  document.addEventListener('click', function(e) {
    // 1. Desktop preview open
    const openBtn = e.target.closest('[data-preview-size-btn]');
    if (openBtn) {
      e.preventDefault();
      const card = openBtn.closest('[data-grid-inner]');
      if (!card) return;

      const topInner = card.querySelector('[data-grid-inner-top]');
      const previewGrid = card.querySelector('[data-preview-size-grid]');
      if (topInner) topInner.classList.add('hidden');
      if (previewGrid) {
        previewGrid.classList.remove('hidden');

        const dialogElement = previewGrid.querySelector('[role="dialog"]');
        if (dialogElement) {
          const header = dialogElement.querySelector('.grid-header');
          if (header) {
            const vendor = header.querySelector('.product-vendor')?.textContent.trim() || '';
            const title = header.querySelector('.product-title')?.textContent.trim() || '';
            const typeLabel = header.querySelector('.product_type_label')?.textContent.trim() || '';
            const labelText = [vendor, title, typeLabel].filter(Boolean).join(' ');
            if (labelText) dialogElement.setAttribute('aria-label', labelText);
          }

          if (typeof _theme !== 'undefined' && typeof _theme.focusTrap === 'function') {
            _theme.focusTrap(dialogElement, () => {
              const closeBtn = previewGrid.querySelector('[data-preview-close-btn]');
              if (closeBtn) closeBtn.click();
            });
          }
        }

        setTimeout(() => {
          const target = dialogElement || previewGrid;
          const focusable = target.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]');
          const visible = Array.from(focusable).filter(el => el.offsetWidth > 0 || el.offsetHeight > 0);
          if (visible.length) visible[0].focus({ preventScroll: true });
        }, 50);
      }
      return;
    }

    // 2. Desktop preview close
    const closeBtn = e.target.closest('[data-preview-close-btn]');
    if (closeBtn) {
      e.preventDefault();
      const card = closeBtn.closest('[data-grid-inner]');
      if (!card) return;

      const previewGrid = card.querySelector('[data-preview-size-grid]');
      const topInner = card.querySelector('[data-grid-inner-top]');

      if (previewGrid) {
        const dialogElement = previewGrid.querySelector('[role="dialog"]');
        if (dialogElement) {
          const tabbedEls = dialogElement.querySelectorAll('.product-vendor, .product-title, .product_type_label');
          tabbedEls.forEach(el => {
            el.removeAttribute('tabindex');
            el.removeAttribute('role');
            el.removeAttribute('aria-level');
            el.removeAttribute('aria-roledescription');
          });
          dialogElement.removeAttribute('aria-label');
          if (typeof _theme !== 'undefined' && typeof _theme.focusTrapReset === 'function') {
            _theme.focusTrapReset(window.jQuery ? jQuery(dialogElement) : dialogElement);
          }
        }
        previewGrid.classList.add('hidden');
      }

      if (topInner) {
        topInner.classList.remove('hidden');
        const cardFooter = topInner.querySelector('.card-footer');
        if (cardFooter) {
          cardFooter.style.opacity = '1';
          cardFooter.style.display = 'flex';
          cardFooter.style.visibility = 'visible';
        }
        const openTrigger = topInner.querySelector('[data-preview-size-btn]');
        if (openTrigger) openTrigger.focus();
      }
      return;
    }

    // 3. Mobile preview open
    const mobOpenBtn = e.target.closest('[data-preview-size-mob-btn]');
    if (mobOpenBtn) {
      e.preventDefault();
      const card = mobOpenBtn.closest('[data-grid-inner]');
      const previewQv = card?.querySelector('.preview-size-qv');
      if (previewQv) {
        previewQv.classList.add('active');

        const variantContent = previewQv.querySelector('.variant-content');
        if (variantContent) {
          const text = variantContent.textContent.trim().replace(/\s+/g, ' ');
          if (text) previewQv.setAttribute('aria-label', text);
        }

        const closeMob = previewQv.querySelector('[data-preview-close-mob-btn]');
        if (closeMob) closeMob.removeAttribute('tabindex');

        if (typeof _theme !== 'undefined' && typeof _theme.focusTrap === 'function') {
          _theme.focusTrap(previewQv, () => {
            if (closeMob) closeMob.click();
          });
        }

        setTimeout(() => {
          const focusable = previewQv.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]');
          const visible = Array.from(focusable).filter(el => el.offsetWidth > 0 || el.offsetHeight > 0);
          if (visible.length) visible[0].focus({ preventScroll: true });
        }, 50);
      }
      return;
    }

    // 4. Mobile preview close
    const mobCloseBtn = e.target.closest('[data-preview-close-mob-btn], .preview-size-qv .qv-close');
    if (mobCloseBtn) {
      e.preventDefault();
      const previewQv = mobCloseBtn.closest('.preview-size-qv');
      const card = mobCloseBtn.closest('[data-grid-inner]');
      if (previewQv) {
        previewQv.removeAttribute('aria-label');
        if (typeof _theme !== 'undefined' && typeof _theme.focusTrapReset === 'function') {
          _theme.focusTrapReset(window.jQuery ? jQuery(previewQv) : previewQv);
        }
        previewQv.classList.remove('active');
      }
      const previewSizeMobBtn = card?.querySelector('[data-preview-size-mob-btn]');
      if (previewSizeMobBtn) previewSizeMobBtn.focus();
      return;
    }

    // 5. Add to Cart
    const atcBtn = e.target.closest('button.preview-atc-btn');
    if (atcBtn) {
      e.preventDefault();
      if (atcBtn.disabled || atcBtn.classList.contains('click-processing')) return;

      const card = atcBtn.closest('[data-grid-inner]');
      const inModal = atcBtn.closest('.preview-size-qv, [data-preview-size-grid]');
      atcTrigger = inModal && card ? (card.querySelector('[data-preview-size-btn], [data-preview-size-mob-btn]') || atcBtn) : atcBtn;

      clearInterval(atcTimer);
      const startTime = Date.now();
      atcTimer = setInterval(() => {
        if (Date.now() - startTime > 5000) return clearInterval(atcTimer);
        if (document.documentElement.classList.contains('cart-show')) {
          if (document.activeElement && document.activeElement.closest('#dropdown-cart')) {
            return clearInterval(atcTimer);
          }
          const cartCloseBtn = document.querySelector('#dropdown-cart .close-cart');
          if (cartCloseBtn) cartCloseBtn.focus();
          clearInterval(atcTimer);
        }
      }, 50);

      atcBtn.classList.add('click-processing');
      atcBtn.disabled = true;

      const varID = card?.querySelector('[data-preview-var-id]')?.getAttribute('data-preview-var-id') || card?.querySelector('input[name="id"]')?.value;
      const qty = parseInt(atcBtn.closest('form')?.querySelector('.quantity__select [data-quantity-select]')?.value) || 1;

      // Support for GWP Discount Bundle Property (_bundleVarId)
      const bundleVarIdEl = card?.closest('.grid-item')?.querySelector('[data-bundleVarId]') || card?.querySelector('[data-bundleVarId]');
      const properties = {};
      if (bundleVarIdEl && bundleVarIdEl.value) {
        properties['_bundleVarId'] = bundleVarIdEl.value;
      }

      const statusAnnounceElem = atcBtn.parentElement?.querySelector('.addedMessageBundleText') || card?.querySelector('.addedMessageBundleText');
      if (statusAnnounceElem) {
        statusAnnounceElem.textContent = 'Please wait, adding product to the cart';
      }

      if (window._theme?.Helpers?.addProductToCart) {
        _theme.toggleLoading(1);
        _theme.Helpers.addProductToCart(varID, qty, properties, function(resp) {
          _theme.MiniCart.lineItemChangeCallBack(resp, function() {
            if (statusAnnounceElem) statusAnnounceElem.textContent = 'Item added to your cart successfully';
            _theme.toggleLoading();
            if (window.jQuery) _theme.toggleSideCart(1, jQuery(atcBtn), true);

            card?.querySelector('.preview-size-qv')?.classList.remove('active');
            card?.querySelector('[data-preview-size-grid]')?.classList.add('hidden');
            card?.querySelector('[data-grid-inner-top]')?.classList.remove('hidden');

            atcBtn.classList.remove('click-processing');
            atcBtn.disabled = false;
          });
        }, function(err) {
          if (_theme.toggleLoading) _theme.toggleLoading();
          atcBtn.classList.remove('click-processing');
          atcBtn.disabled = false;
        });
      } else {
        atcBtn.classList.remove('click-processing');
        atcBtn.disabled = false;
      }
    }
  });
};

// Global compatibility class for any existing callers (e.g. searchanize-products.js)
window.productPreviewSizes = class {
  constructor() {
    window.initProductPreviewSizes();
  }
};

// Initialize immediately and on DOMContentLoaded
window.initProductPreviewSizes();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.initProductPreviewSizes);
}

// Card footer tabIndex management for responsive keyboard navigation (ADA)
function manageCardFooterTabIndex() {
  const isDesktop = window.innerWidth >= 768;
  const cardFooters = document.querySelectorAll("[data-grid-inner] .product-bottom .card-footer");
  cardFooters.forEach(cardFooter => {
    cardFooter.tabIndex = isDesktop ? 0 : -1;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', manageCardFooterTabIndex);
} else {
  manageCardFooterTabIndex();
}

let resizeDebounce;
window.addEventListener('resize', function() {
  clearTimeout(resizeDebounce);
  resizeDebounce = setTimeout(manageCardFooterTabIndex, 150);
});

